<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch, type PropType } from 'vue'
import { BarChartSorting, SolidFill, ColorHEX, emptyLine, FontSettings, AxisTickStrategies, Themes } from '@lightningchart/lcjs'
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue'
const { hasIntermediateAccess } = useAuth();

interface TotalMaterialConsumptionFromEventsItem {  
  cement: number
  additive1: number
  additive2: number
  additive3: number
  accelerator: number
  colloidal :number
  colloidalSlurry: number
  water: number
  powerConsumptionskWh: number
}

interface TotalConsumedMaterialOverviewFromPumpsItem {
  holeNo: number
  maxPressure: number
  cement: number
  additive1: number
  additive2: number
  additive3: number
  accelerator: number
}

const props = defineProps({
  totalUsedMaterialFromEvents: {
    type: Object as PropType<TotalMaterialConsumptionFromEventsItem>,
    required: true,
    default: () => ({})
  },
  consumedMaterialOverviewFromPumps: {
    type: Array as PropType<TotalConsumedMaterialOverviewFromPumpsItem[]>,
    required: true
  }
});

const fmt = (val: number | undefined) => {
  if (val === undefined || val === null) return '0';
  return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const extractTotalMaterialConsumedFromPumps = computed(() => {
  const total = {
    cement: 0,
    additive1: 0,
    additive2: 0,
    additive3: 0,
    accelerator: 0
  };
  for (const item of props.consumedMaterialOverviewFromPumps) 
  {
    total.cement += item.cement || 0;
    total.additive1 += item.additive1 || 0;
    total.additive2 += item.additive2 || 0;
    total.additive3 += item.additive3 || 0;
    total.accelerator += item.accelerator || 0;
  }
  return total;
});

const headers = [
  { title: 'Hole No', key: 'holeNo', align: 'start' },
  { title: 'Max Pressure (bar)', key: 'maxPressure', align: 'end' },
  { title: 'Cement (kg)', key: 'cement', align: 'end' },
  { title: 'Accelerator (kg)', key: 'accelerator', align: 'end' },
  { title: 'Additive 1 (L)', key: 'additive1', align: 'end' },
  { title: 'Additive 2 (L)', key: 'additive2', align: 'end' },
  { title: 'Additive 3 (L)', key: 'additive3', align: 'end' },
] as const;

// --- Bar chart ---
const chartContainer = ref<HTMLDivElement>()
let chart: any = undefined

const barData = computed(() => {
  if (!props.consumedMaterialOverviewFromPumps?.length) return []
  return props.consumedMaterialOverviewFromPumps.map(item => ({
    category: `H${item.holeNo}`,
    value: item.cement || 0
  }))
})

const initChart = () => {
  if (!chartContainer.value) return
  const lc = useUseLightningCharts()

  const AMV_BLUE   = ColorHEX('#041e43')
  const TEXT_DARK  = ColorHEX('#1e2d3d')
  const TEXT_MID   = ColorHEX('#4b5e6d')
  const PAGE_BG    = ColorHEX('#f5f5f5')

  const axisLabelFont = new FontSettings().setSize(11).setFamily('Arial').setWeight('normal')
  const titleFont     = new FontSettings().setSize(15).setFamily('Arial').setWeight('bold')

  chart = lc.BarChart({ container: chartContainer.value, theme: Themes.light })
    .setTitle('Total Cement per Hole (kg)')
    .setTitleFont(titleFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_DARK }))
    .setTitleEffect(false)
    .setBackgroundFillStyle(new SolidFill({ color: PAGE_BG }))
    .setBackgroundStrokeStyle(emptyLine)
    .setSeriesBackgroundFillStyle(new SolidFill({ color: ColorHEX('#ffffff') }))
    .setSeriesBackgroundEffect(false)
    .setCornerRadius(3)
    .setBarsMargin(0.05)
    .setBarsEffect(false)
    .setAnimationsEnabled(false)
    .setSorting(BarChartSorting.Disabled)
    .setValueLabels(undefined)
    .setCategoryLabels({
      labelFont: axisLabelFont,
      labelFillStyle: new SolidFill({ color: TEXT_MID })
    })

  chart.valueAxis
    .setTickStrategy(AxisTickStrategies.Numeric, (ticks: any) =>
      ticks.setMajorTickStyle((major: any) =>
        major
          .setLabelFont(axisLabelFont)
          .setLabelFillStyle(new SolidFill({ color: TEXT_MID }))
      )
    )
    .setTitle('kg')
    .setTitleFont(axisLabelFont)
    .setTitleFillStyle(new SolidFill({ color: TEXT_MID }))

  if (barData.value.length > 0) {
    applyChartData(barData.value)
  }
}

onMounted(() => {
  initChart()
})

onUnmounted(() => {
  chart?.dispose()
  chart = undefined
})

const applyChartData = (data: { category: string; value: number }[]) => {
  chart.setData(data)
  chart.getSeries().forEach((bar: any) => {
    bar.setFillStyle(new SolidFill({ color: ColorHEX('#041e43') }))
       .setStrokeStyle(emptyLine)
  })
}

watch(barData, (newData) => {
  if (!chart || !newData.length) return
  applyChartData(newData)
})

</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-container fluid v-else>

    <v-row class="mb-6">
      <!-- Total Consumption (Pumps) -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Total Consumption (Pumps)
          </v-card-title>
          <v-divider></v-divider>
          
          <v-card-text class="pa-6">
            <v-row dense v-if="Object.keys(extractTotalMaterialConsumedFromPumps).length > 0">
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Cement</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.cement) }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Additive 1</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.additive1) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Additive 2</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.additive2) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Additive 3</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.additive3) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Accelerator</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(extractTotalMaterialConsumedFromPumps.accelerator) }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
            </v-row>
            <div v-else class="text-center text-medium-emphasis font-italic py-4">No pump data</div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Event Totals -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2">
          <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
            Total Consumption (Events/Scale)
          </v-card-title>
          <v-divider></v-divider>
          
          <v-card-text class="pa-6">
            <v-row dense v-if="Object.keys(totalUsedMaterialFromEvents).length > 0">
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Cement</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.cement) }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Additive 1</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.additive1) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Additive 2</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.additive2) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Additive 3</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.additive3) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Accelerator</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.accelerator) }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Water</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.water) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Colloidal</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.colloidal) }} <span class="text-caption text-medium-emphasis">kg</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Slurry</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.colloidalSlurry) }} <span class="text-caption text-medium-emphasis">L</span></div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="text-caption text-uppercase font-weight-bold text-medium-emphasis">Power</div>
                <div class="text-h6 font-weight-bold text-blue-grey-darken-3 font-monospace">{{ fmt(totalUsedMaterialFromEvents.powerConsumptionskWh) }} <span class="text-caption text-medium-emphasis">kWh</span></div>
              </v-col>
            </v-row>
            <div v-else class="text-center text-medium-emphasis font-italic py-4">No event data</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Chart Section -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="text-subtitle-1 font-weight-bold bg-grey-lighten-4">
        Consumption Overview
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-0">
        <div ref="chartContainer" style="height: 350px; width: 100%;"></div>
      </v-card-text>
    </v-card>

    <!-- Detailed Table -->
    <v-card elevation="2">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 bg-grey-lighten-4">
        <span class="text-subtitle-1 font-weight-bold">Consumption per Hole</span>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="props.consumedMaterialOverviewFromPumps"
        density="compact"
        class="text-body-2"
        hover
      >
        <template v-slot:item.holeNo="{ item }">
          <v-chip size="small" variant="flat" color="blue-lighten-5" class="text-blue-darken-2 font-weight-bold">
            {{ item.holeNo }}
          </v-chip>
        </template>

        <!-- Format all numeric columns -->
        <template v-slot:item.maxPressure="{ item }"><span class="font-monospace">{{ fmt(item.maxPressure) }}</span></template>
        <template v-slot:item.cement="{ item }"><span class="font-monospace">{{ fmt(item.cement) }}</span></template>
        <template v-slot:item.accelerator="{ item }"><span class="font-monospace">{{ fmt(item.accelerator) }}</span></template>
        <template v-slot:item.additive1="{ item }"><span class="font-monospace">{{ fmt(item.additive1) }}</span></template>
        <template v-slot:item.additive2="{ item }"><span class="font-monospace">{{ fmt(item.additive2) }}</span></template>
        <template v-slot:item.additive3="{ item }"><span class="font-monospace">{{ fmt(item.additive3) }}</span></template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.font-monospace {
  font-family: 'Courier New', Courier, monospace !important;
}
</style>